import * as THREE from "three";
import { INDOOR_FIBREBOARD_T_M } from "../../domain/indoor/indoorRoom";
import {
	ENGINE_CLADDING_T_M as CLAD_T,
	ENGINE_CORNER_BOARD_W_M as CORNER_BOARD_W_M,
	ENGINE_BASE_TRIM_H_M,
	ENGINE_BASE_TRIM_TOP_ABOVE_BASE_M,
	ENGINE_WIND_BARRIER_T_M,
	ENGINE_FURRING_D_M as FURR_D,
	ENGINE_FURRING_W_M as FURR_W,
	ENGINE_STUD_W_M as STUD_W,
} from "../constants";
import { addOpeningTrimFrame } from "../detailHelpers";
import { addBox } from "../geometry";
import {
	matCornerTrim,
	matCornerTrimHorizontal,
	matExteriorCladding,
	matPanel,
	matPanelLying,
	matWindBarrier,
	matWood,
} from "../materials";
import {
	renderExteriorCladdingStrips,
	renderFurringStrips,
	renderHorizontalFurringStrips,
	renderOuterLayerStrips,
	renderPanelStrips,
} from "../wallStrips";
import type {
	ExteriorFacadeBuildOptions,
	PanelBuildOptions,
	SquareBaseBuildContext,
	SquareBaseWall,
	WallFramingResult,
} from "./context";

export function buildPanels(
	ctx: SquareBaseBuildContext,
	framingResults: Map<SquareBaseWall, WallFramingResult>,
	panelGroup: THREE.Group,
	frontPanelGroup: THREE.Group,
	options: PanelBuildOptions,
) {
	const panelMaterial =
		options.panelDirection === "lying" ? matPanelLying : matPanel;
	if (options.mode === "striped") {
		for (const wall of ["left", "right", "front", "back"] as const) {
			const result = framingResults.get(wall)!;
			renderPanelStrips(
				{
					wall,
					W: ctx.W,
					L: ctx.L,
					wallBase: ctx.wallBase,
					roofGeometry: ctx.roofGeometry,
					offset: 0,
					// The bastupanel is nailed to the LYING glespanel, so its back
					// face sits one folie + one batten depth in from the studs.
					innerOffset: ctx.iL,
				renderDoorGlass: options.renderDoorGlass,
					material: panelMaterial,
					group: wall === "front" ? frontPanelGroup : panelGroup,
				},
				result.surfaceOpenings,
				ctx.roofGeometry.getMaxWallHeight(wall),
			);
		}
		return;
	}

	for (const wall of ["left", "right", "front", "back"] as const) {
		const result = framingResults.get(wall)!;
		renderPanelStrips(
			{
				wall,
				W: ctx.W,
				L: ctx.L,
				wallBase: ctx.wallBase,
				roofGeometry: ctx.roofGeometry,
				offset: 0,
				innerOffset: ctx.iL,
				renderDoorGlass: options.renderDoorGlass,
				material: panelMaterial,
				group: wall === "front" ? frontPanelGroup : panelGroup,
			},
			result.surfaceOpenings,
			ctx.roofGeometry.getMaxWallHeight(wall),
		);
	}
}

/**
 * A panorama glazing wall is split into two panes with a stud-wide gap so the
 * mullion stud can be rendered between them (see
 * docs/notes/2026-06-23-two-room-panorama-fixtures-must-avoid-the-glass-wall.md
 * and `getGlazingZonesForWall` in twoRoomSauna.ts, which reserves
 * `Math.round(studWidthMm / 2)` on each side of the divider — a ~46mm gap for
 * the default 45mm stud). If each pane then draws its own full exterior foder
 * frame, the two mullion-facing jambs — plus the dripcap/sill that overhang
 * past each opening edge — land in the same plane and z-fight (dark moiré
 * vertical strip at the partition/glass-wall junction). A real mullioned
 * window unit has ONE continuous outer frame with the mullion as the only
 * divider, so merge the trim for openings that sit within a mullion-gap of
 * each other and let the single frame span both.
 *
 * The threshold MUST be tight — it targets only the mullion case, not two
 * independently placed windows a user happens to put close together on the
 * same wall (same height, small gap is a valid layout; merging those would
 * draw one head/sill spanning solid wall with no jambs between them). Derive
 * it from the actual stud width used to build the mullion gap rather than a
 * fixed pixel-ish constant: 1.5× stud width comfortably covers the ~46mm
 * mullion gap while still excluding realistic adjacent-window gaps (tested
 * down to 100mm). Isolated openings pass through unchanged, so normal
 * single-window / single-door walls are unaffected.
 */
const MULLION_TRIM_MERGE_GAP_MM = Math.round(STUD_W * 1000 * 1.5);

function mergeOpeningsForTrim(
	openings: WallFramingResult["surfaceOpenings"],
): WallFramingResult["surfaceOpenings"] {
	if (openings.length < 2) return openings;
	const sorted = [...openings].sort((a, b) => a.xMm - b.xMm);
	const merged: WallFramingResult["surfaceOpenings"] = [];
	for (const opening of sorted) {
		const prev = merged[merged.length - 1];
		const sameBand =
			prev !== undefined &&
			prev.type === opening.type &&
			Math.abs(prev.yMm - opening.yMm) < 1 &&
			Math.abs(prev.heightMm - opening.heightMm) < 1;
		const gap = prev
			? opening.xMm - (prev.xMm + prev.widthMm)
			: Number.POSITIVE_INFINITY;
		if (sameBand && gap <= MULLION_TRIM_MERGE_GAP_MM && gap >= -1) {
			const start = Math.min(prev.xMm, opening.xMm);
			const end = Math.max(
				prev.xMm + prev.widthMm,
				opening.xMm + opening.widthMm,
			);
			merged[merged.length - 1] = {
				...prev,
				id: `${prev.id}+${opening.id}`,
				xMm: start,
				widthMm: end - start,
			};
		} else {
			merged.push({ ...opening });
		}
	}
	return merged;
}

function addExteriorOpeningTrimForWall(
	ctx: SquareBaseBuildContext,
	group: THREE.Group,
	wall: SquareBaseWall,
	opening: WallFramingResult["wallOpenings"][number],
	options: {
		claddingOffset: number;
		claddingThickness: number;
		exteriorTrimDepth: number;
		exteriorTrimWidth: number;
		namePrefix: string;
	},
) {
	const width = opening.widthMm / 1000;
	const height = opening.heightMm / 1000;
	const y = ctx.wallBase + opening.yMm / 1000 + height / 2;

	// The frame may never extend into the knutbrädor at the wall ends — a
	// full-wall panorama opening otherwise pushes its jambs, drip cap and
	// sill straight through the corner boards. The boards sit on the
	// CLADDING plane (addInterlockingCornerBoards), so their inner edge in
	// wall-local coordinates is CORNER_BOARD_W_M minus the facade buildout —
	// clamping to the bare board width instead leaves an exposed gap
	// between the trim end and the corner board. Expressed relative to the
	// opening centre for addOpeningTrimFrame's axisSpan clamp; ordinary
	// openings sit well inside (corner clearance rules) and are unaffected.
	const CORNER_TRIM_CLEARANCE_M = 0.003;
	const cornerBoardInsetM = Math.max(
		0,
		CORNER_BOARD_W_M - options.claddingOffset - options.claddingThickness / 2,
	);
	const wallLengthM = wall === "front" || wall === "back" ? ctx.W : ctx.L;
	const openingCenterAxisM = opening.xMm / 1000 + width / 2;
	const axisSpan = {
		startM: cornerBoardInsetM + CORNER_TRIM_CLEARANCE_M - openingCenterAxisM,
		endM:
			wallLengthM -
			cornerBoardInsetM -
			CORNER_TRIM_CLEARANCE_M -
			openingCenterAxisM,
	};

	if (wall === "front" || wall === "back") {
		const claddingOuterFaceZ =
			wall === "front"
				? ctx.L / 2 + options.claddingOffset + options.claddingThickness / 2
				: -ctx.L / 2 - options.claddingOffset - options.claddingThickness / 2;
		addOpeningTrimFrame({
			group,
			width,
			height,
			depth: options.exteriorTrimDepth,
			trimWidth: options.exteriorTrimWidth,
			includeBottom: opening.type !== "door",
			material: matCornerTrim,
			horizontalMaterial: matCornerTrimHorizontal,
			position: new THREE.Vector3(
				-ctx.W / 2 + opening.xMm / 1000 + width / 2,
				y,
				wall === "front"
					? claddingOuterFaceZ + options.exteriorTrimDepth / 2 + 0.002
					: claddingOuterFaceZ - options.exteriorTrimDepth / 2 - 0.002,
			),
			orientation: "z",
			namePrefix: options.namePrefix,
			dripCap: true,
			sill: opening.type !== "door",
			outwardSign: wall === "front" ? 1 : -1,
			axisSpan,
		});
		return;
	}

	const claddingOuterFaceX =
		wall === "left"
			? -ctx.W / 2 - options.claddingOffset - options.claddingThickness / 2
			: ctx.W / 2 + options.claddingOffset + options.claddingThickness / 2;
	addOpeningTrimFrame({
		group,
		width,
		height,
		depth: options.exteriorTrimDepth,
		trimWidth: options.exteriorTrimWidth,
		includeBottom: opening.type !== "door",
		material: matCornerTrim,
		horizontalMaterial: matCornerTrimHorizontal,
		position: new THREE.Vector3(
			wall === "left"
				? claddingOuterFaceX - options.exteriorTrimDepth / 2 - 0.002
				: claddingOuterFaceX + options.exteriorTrimDepth / 2 + 0.002,
			y,
			-ctx.L / 2 + opening.xMm / 1000 + width / 2,
		),
		orientation: "x",
		namePrefix: options.namePrefix,
		dripCap: true,
		sill: opening.type !== "door",
		outwardSign: wall === "right" ? 1 : -1,
		axisSpan,
	});
}

/**
 * Face width of a window unit's karm, seen from outside. A slim fixed-glass
 * profile; wide enough to read as a frame, narrow enough to keep the glass
 * the subject.
 */
export const WINDOW_FRAME_FACE_M = 0.035;
/** Keep the karm this far off every framing face it sits against. */
const WINDOW_FRAME_CLEARANCE_M = 0.0005;

/**
 * The window unit's own frame (karm), lining the rough opening from the glass
 * out to the facade.
 *
 * The glass is drawn at the interior panel plane, so from outside every window
 * sits at the bottom of a ~190 mm deep reveal. Without a karm that reveal
 * showed the bare construction: stud and cripple end grain, insulation edges,
 * furring and the wind barrier — pale raw lumber framing the glass, which is
 * exactly what made a black panorama facade read as unfinished. A bought
 * window (and a fixed panorama unit) comes with its karm, so this is geometry
 * of the product the `Fönster` row already buys, not a new material.
 *
 * Every opening gets its own frame (NOT the mullion-merged trim set): the
 * cladding strip between two panorama panes then reads as the mullion, lined
 * on both sides. Uses the facade trim material, so it follows the exterior
 * treatment — black under slamfärg svart, like the foder around it.
 */
function addWindowFrameForWall(
	ctx: SquareBaseBuildContext,
	group: THREE.Group,
	wall: SquareBaseWall,
	opening: WallFramingResult["surfaceOpenings"][number],
	options: { facadeOuterOffsetM: number; namePrefix: string },
) {
	const face = WINDOW_FRAME_FACE_M;
	const clear = WINDOW_FRAME_CLEARANCE_M;
	const alongX = wall === "front" || wall === "back";
	const wallLength = alongX ? ctx.W : ctx.L;
	// A full-wall panorama opening runs into the corners, where the crossing
	// wall's end stud stands. The glass stops at that stud's inner face
	// (wallStrips' inner surface range), so the karm does too.
	const start = Math.max(opening.xMm / 1000, ctx.sD) + clear;
	const end =
		Math.min((opening.xMm + opening.widthMm) / 1000, wallLength - ctx.sD) -
		clear;
	const width = end - start;
	const height = opening.heightMm / 1000 - 2 * clear;
	if (width <= 2 * face || height <= 2 * face) return;

	const halfSpan = alongX ? ctx.L / 2 : ctx.W / 2;
	// Glass/panel outer face → cladding outer face, measured from the centre.
	const innerPlane = halfSpan - ctx.sD - ctx.iL + clear;
	const outerPlane = halfSpan + options.facadeOuterOffsetM - clear;
	const depth = outerPlane - innerPlane;
	if (depth <= 0) return;
	const normalCenter = (innerPlane + outerPlane) / 2;
	const outward = wall === "front" || wall === "right" ? 1 : -1;
	const axisCenter = -wallLength / 2 + (start + end) / 2;
	const yCenter =
		ctx.wallBase + opening.yMm / 1000 + opening.heightMm / 1000 / 2;

	// Where the opening ran into a corner, the jamb also fills the slot
	// between the corner board and the glass, in front of the crossing
	// wall's end stud — otherwise that stud shows as a pale strip beside
	// the glass.
	const startReach = Math.max(0, start - opening.xMm / 1000 - clear);
	const endReach = Math.max(
		0,
		(opening.xMm + opening.widthMm) / 1000 - clear - end,
	);
	const pieces = [
		// Jambs run the full height; head and sill fit between them.
		{
			part: "left",
			axis: -(width - face) / 2 - startReach / 2,
			y: 0,
			w: face + startReach,
			h: height,
		},
		{
			part: "right",
			axis: (width - face) / 2 + endReach / 2,
			y: 0,
			w: face + endReach,
			h: height,
		},
		{
			part: "top",
			axis: 0,
			y: (height - face) / 2,
			w: width - 2 * face,
			h: face,
		},
		{
			part: "bottom",
			axis: 0,
			y: -(height - face) / 2,
			w: width - 2 * face,
			h: face,
		},
	];
	for (const piece of pieces) {
		const lying = piece.part === "top" || piece.part === "bottom";
		const material = lying ? matCornerTrimHorizontal : matCornerTrim;
		const along = axisCenter + piece.axis;
		const across = outward * normalCenter;
		const mesh = alongX
			? addBox(
					piece.w,
					piece.h,
					depth,
					material,
					along,
					yCenter + piece.y,
					across,
					group,
				)
			: addBox(
					depth,
					piece.h,
					piece.w,
					material,
					across,
					yCenter + piece.y,
					along,
					group,
				);
		// A glazed door's karm belongs to the door product (issue 160).
		const family = opening.type === "door" ? "door-frame" : "window-frame";
		mesh.name = `detail/${family}/${options.namePrefix}-${piece.part}`;
	}
}

function isGlazedWindow(
	opening: WallFramingResult["surfaceOpenings"][number],
): boolean {
	return opening.type === "window" || opening.glazed === true;
}

/**
 * Paired, interlocking corner boards (knutbrädor) at all four corners.
 *
 * Real Swedish knutbrädor read ~70-95mm wide per exposed face — 90mm
 * lands in that range for both the wrapping face board and the board
 * that tucks in behind it. The boards must interlock like a real
 * overlapping corner joint instead of butting flush at the exact same
 * plane — see docs/notes/2026-04-10-exterior-opening-trim-must-sit-proud-of-cladding.md
 * (2026-07-16 addendum). `frontBackTrim` wraps past the geometric corner
 * by the side board's thickness plus a small clearance so it is
 * unambiguously the outer, visible surface at the wrap; `sideTrim`
 * extends the same distance into the corner but stays that same
 * clearance short of frontBackTrim's outer faces, so it tucks in behind
 * the wrap (occluded, not coplanar). This produces a genuine
 * positive-depth overlap with no shared coplanar exposed faces.
 *
 * Shared by the ventilated and cladding-only facade modes — the
 * cladding-only path used to draw a single 90×50mm post per corner
 * instead, which read as one misplaced plank rather than two boards
 * meeting at the corner.
 */
function addInterlockingCornerBoards(
	ctx: SquareBaseBuildContext,
	exteriorGroup: THREE.Group,
	frontExteriorGroup: THREE.Group,
	options: {
		claddingOffset: number;
		namePrefix: string;
		/**
		 * Drop the boards this far below wallBase so they run unbroken to
		 * the base trim's bottom drip edge — the base trim occupies the same
		 * depth slab and butts against the boards instead of running through
		 * their bottom ends.
		 */
		bottomExtensionM?: number;
		/**
		 * Walls whose base trim is suppressed (deck attached). The two corners
		 * adjacent to such a wall drop their bottom extension so the boards
		 * don't dangle into the deck instead of butting a base trim that isn't
		 * there.
		 */
		suppressedWalls?: SquareBaseWall[];
	},
) {
	const trimW = CORNER_BOARD_W_M;
	const trimD = CLAD_T;
	const bottomExt = options.bottomExtensionM ?? 0;
	const suppressed = new Set(options.suppressedWalls ?? []);
	const cladOuterFaceX = ctx.W / 2 + options.claddingOffset + CLAD_T / 2;
	const cladOuterFaceZ = ctx.L / 2 + options.claddingOffset + CLAD_T / 2;
	const CORNER_OVERLAP_CLEARANCE_M = 0.002;
	const wrapExt = trimD + CORNER_OVERLAP_CLEARANCE_M;
	const recessExt = trimD - CORNER_OVERLAP_CLEARANCE_M;
	[
		[-1, -1],
		[-1, 1],
		[1, -1],
		[1, 1],
	].forEach(([sx, sz]) => {
		const cH = ctx.roofGeometry.getRoofHeightAt(
			(sx * ctx.W) / 2,
			(sz * ctx.L) / 2,
		);
		const targetGroup = sz === 1 ? frontExteriorGroup : exteriorGroup;
		const cornerFaceX = sx * cladOuterFaceX;
		const cornerFaceZ = sz * cladOuterFaceZ;

		// A corner is adjacent to two walls: the front/back wall it sits on and
		// the side wall. Each board drops its bottom extension only when ITS
		// wall has the base trim suppressed (deck attached), so it doesn't
		// dangle into the deck. The board on the other wall keeps it: that
		// wall's base trim still butts into it, and dropping it opened a slot
		// at the corner showing the floor joists' ends.
		const cornerFrontBackWall: SquareBaseWall = sz === 1 ? "front" : "back";
		const cornerSideWall: SquareBaseWall = sx === -1 ? "left" : "right";
		const faceSuppressed = suppressed.has(cornerFrontBackWall);
		const sideSuppressed = suppressed.has(cornerSideWall);
		const faceBottomExt = faceSuppressed ? 0 : bottomExt;
		const sideBottomExt = sideSuppressed ? 0 : bottomExt;

		// frontBackTrim: unchanged near edge (trimW into the front/back
		// wall), far edge wraps past the corner by wrapExt so it fully
		// covers the notch that used to be left uncovered between the
		// two boards.
		const frontBackTrimWidth = trimW + wrapExt;
		const frontBackTrim = addBox(
			frontBackTrimWidth,
			cH + faceBottomExt,
			trimD,
			matCornerTrim,
			cornerFaceX + (sx * (wrapExt - trimW)) / 2,
			ctx.wallBase + (cH - faceBottomExt) / 2,
			sz * (cladOuterFaceZ + trimD / 2),
			targetGroup,
		);
		frontBackTrim.name = `detail/silhouette/${options.namePrefix}-corner-face-${sx === -1 ? "left" : "right"}-${sz === -1 ? "back" : "front"}`;

		// sideTrim: unchanged far edge (trimW into the side wall), near
		// edge extends recessExt past the corner — just short of
		// frontBackTrim's outer faces — so it overlaps frontBackTrim's
		// volume without ever sharing an exposed coplanar face with it.
		const sideTrimDepth = trimW + recessExt;
		const sideTrim = addBox(
			trimD,
			cH + sideBottomExt,
			sideTrimDepth,
			matCornerTrim,
			sx * (cladOuterFaceX + trimD / 2),
			ctx.wallBase + (cH - sideBottomExt) / 2,
			cornerFaceZ + (sz * (recessExt - trimW)) / 2,
			targetGroup,
		);
		const cornerName = `${options.namePrefix}-corner-side-${sx === -1 ? "left" : "right"}-${sz === -1 ? "back" : "front"}`;
		sideTrim.name = `detail/silhouette/${cornerName}`;

		// A deck on one wall of this corner starts at that wall's nominal end,
		// short of the facade corner. Below wallBase the strip between the two
		// is still open, so the suppressed wall's board keeps a foot there —
		// from its outer edge back to where the deck begins, never into it.
		// A corner with decks on both walls is covered by the deck itself.
		if (bottomExt > 0 && sideSuppressed !== faceSuppressed) {
			const footY = ctx.wallBase - bottomExt / 2;
			if (sideSuppressed) {
				const outer = cornerFaceZ + sz * recessExt;
				const inner = (sz * ctx.L) / 2;
				addBox(
					trimD,
					bottomExt,
					Math.abs(outer - inner),
					matCornerTrim,
					sx * (cladOuterFaceX + trimD / 2),
					footY,
					(outer + inner) / 2,
					targetGroup,
				).name = `detail/silhouette/${cornerName}-foot`;
			} else {
				const outer = cornerFaceX + sx * wrapExt;
				const inner = (sx * ctx.W) / 2;
				addBox(
					Math.abs(outer - inner),
					bottomExt,
					trimD,
					matCornerTrim,
					(outer + inner) / 2,
					footY,
					sz * (cladOuterFaceZ + trimD / 2),
					targetGroup,
				).name =
					`detail/silhouette/${cornerName.replace("-corner-side-", "-corner-face-")}-foot`;
			}
		}
	});
}

export function buildExteriorFacade(
	ctx: SquareBaseBuildContext,
	framingResults: Map<SquareBaseWall, WallFramingResult>,
	exteriorGroup: THREE.Group,
	frontExteriorGroup: THREE.Group,
	options: ExteriorFacadeBuildOptions,
) {
	// Standard Swedish exterior fönster-/dörrfoder ≈ 22×95 mm. Earlier
	// values (60 mm depth × 40 mm width) read as a chunky 3D moulding
	// sticking far out of the wall — neither realistic nor aligned with
	// how a 22×95 mm board actually looks on a sauna.
	const exteriorTrimDepth = 0.022;
	const exteriorTrimWidth = 0.07;

	if (options.mode === "cladding-only") {
		const exteriorOffset = CLAD_T / 2;
		for (const wall of ["front", "back", "left", "right"] as const) {
			const result = framingResults.get(wall)!;
			renderExteriorCladdingStrips(
				{
					wall,
					W: ctx.W,
					L: ctx.L,
					wallBase: ctx.wallBase,
					roofGeometry: ctx.roofGeometry,
					offset: exteriorOffset,
					material: matExteriorCladding,
					group: wall === "front" ? frontExteriorGroup : exteriorGroup,
					// Panel first, knutbräda on top: front/back cladding wraps
					// past the corner to the side walls' outer face; the side
					// cladding already ends flush against its back side here
					// (the cladding sits directly on the studs in this mode).
					cornerExtensionM: wall === "front" || wall === "back" ? CLAD_T : 0,
				},
				result.surfaceOpenings,
				ctx.roofGeometry.getMaxWallHeight(wall),
				CLAD_T,
			);
		}

		if (options.renderOpeningTrim !== false) {
			for (const wall of ["front", "back", "left", "right"] as const) {
				const result = framingResults.get(wall);
				if (!result) continue;
				for (const opening of mergeOpeningsForTrim(result.surfaceOpenings)) {
					addExteriorOpeningTrimForWall(
						ctx,
						wall === "front" ? frontExteriorGroup : exteriorGroup,
						wall,
						opening,
						{
							claddingOffset: exteriorOffset,
							claddingThickness: CLAD_T,
							exteriorTrimDepth,
							exteriorTrimWidth,
							namePrefix:
								wall === "front"
									? `${options.namePrefix}-front-facade-door-${opening.id}`
									: `${options.namePrefix}-facade-${wall}-${opening.type}-${opening.id}`,
						},
					);
				}
				for (const opening of result.surfaceOpenings) {
					if (!isGlazedWindow(opening)) continue;
					addWindowFrameForWall(
						ctx,
						wall === "front" ? frontExteriorGroup : exteriorGroup,
						wall,
						opening,
						{
							facadeOuterOffsetM: exteriorOffset + CLAD_T / 2,
							namePrefix: `${options.namePrefix}-${wall}-${opening.id}`,
						},
					);
				}
			}
		}

		if (options.includeCornerTrim) {
			addInterlockingCornerBoards(ctx, exteriorGroup, frontExteriorGroup, {
				claddingOffset: exteriorOffset,
				namePrefix: options.namePrefix,
			});
		}

		return { wbThick: 0 };
	}

	const wbThick = ENGINE_WIND_BARRIER_T_M;
	const wbOffset = wbThick / 2;
	for (const wall of ["front", "back", "left", "right"] as const) {
		renderOuterLayerStrips(
			{
				wall,
				W: ctx.W,
				L: ctx.L,
				wallBase: ctx.wallBase,
				roofGeometry: ctx.roofGeometry,
				offset: wbOffset,
				material: matWindBarrier,
				group: wall === "front" ? frontExteriorGroup : exteriorGroup,
				renderGlass: false,
			},
			framingResults.get(wall)!.surfaceOpenings,
			ctx.roofGeometry.getMaxWallHeight(wall),
			wbThick,
		);
	}

	for (const wall of ["front", "back", "left", "right"] as const) {
		const result = framingResults.get(wall)!;
		renderFurringStrips(
			{
				wall,
				W: ctx.W,
				L: ctx.L,
				wallBase: ctx.wallBase,
				roofGeometry: ctx.roofGeometry,
				offset: wbThick + FURR_D / 2,
				material: matWood,
				group: wall === "front" ? frontExteriorGroup : exteriorGroup,
			},
			result.studAxisPositions,
			result.surfaceOpenings,
			FURR_W,
			FURR_D,
			matWood,
		);
	}

	// Liggande spikläkt (horizontal nailing battens) — the standing panel is
	// nailed into THESE, not into the standing ströläkt below. Sits one furring
	// depth further out than the ströläkt (korsläkt), c/c 600 up the height.
	const horizontalFurringOffset = wbThick + FURR_D + FURR_D / 2;
	for (const wall of ["front", "back", "left", "right"] as const) {
		const result = framingResults.get(wall)!;
		renderHorizontalFurringStrips(
			{
				wall,
				W: ctx.W,
				L: ctx.L,
				wallBase: ctx.wallBase,
				roofGeometry: ctx.roofGeometry,
				offset: horizontalFurringOffset,
				material: matWood,
				group: wall === "front" ? frontExteriorGroup : exteriorGroup,
			},
			result.surfaceOpenings,
			FURR_W,
			FURR_D,
			matWood,
		);
	}

	const cladOffset = wbThick + FURR_D + FURR_D + CLAD_T / 2;
	for (const wall of ["front", "back", "left", "right"] as const) {
		renderExteriorCladdingStrips(
			{
				wall,
				W: ctx.W,
				L: ctx.L,
				wallBase: ctx.wallBase,
				roofGeometry: ctx.roofGeometry,
				offset: cladOffset,
				material: matExteriorCladding,
				group: wall === "front" ? frontExteriorGroup : exteriorGroup,
				// Panel first, knutbräda on top: the cladding plane sits
				// wbThick + furring outside the studs, so strips that stop at
				// the stud rectangle leave an open L-shaped notch at every
				// corner with bare furring/framing visible past the corner
				// board's edge. Wrap the front/back cladding to the side
				// walls' outer cladding face and butt the side cladding into
				// the front/back cladding's inner face, like a real facade.
				cornerExtensionM:
					wall === "front" || wall === "back"
						? cladOffset + CLAD_T / 2
						: cladOffset - CLAD_T / 2,
			},
			framingResults.get(wall)!.surfaceOpenings,
			ctx.roofGeometry.getMaxWallHeight(wall),
			CLAD_T,
		);
	}

	if (options.renderOpeningTrim !== false) {
		for (const wall of ["front", "back", "left", "right"] as const) {
			for (const opening of mergeOpeningsForTrim(
				framingResults.get(wall)!.surfaceOpenings,
			)) {
				addExteriorOpeningTrimForWall(
					ctx,
					wall === "front" ? frontExteriorGroup : exteriorGroup,
					wall,
					opening,
					{
						claddingOffset: cladOffset,
						claddingThickness: CLAD_T,
						exteriorTrimDepth,
						exteriorTrimWidth,
						namePrefix: `${wall}-${opening.type}-${opening.xMm}`,
					},
				);
			}
			for (const opening of framingResults.get(wall)!.surfaceOpenings) {
				if (!isGlazedWindow(opening)) continue;
				addWindowFrameForWall(
					ctx,
					wall === "front" ? frontExteriorGroup : exteriorGroup,
					wall,
					opening,
					{
						facadeOuterOffsetM: cladOffset + CLAD_T / 2,
						namePrefix: `${wall}-${opening.id}`,
					},
				);
			}
		}
	}

	const baseTrimH = ENGINE_BASE_TRIM_H_M;
	const baseTrimTopAboveWallBase = ENGINE_BASE_TRIM_TOP_ABOVE_BASE_M;
	const suppressedWalls = options.baseTrimSuppressedWalls ?? [];
	const suppressed = new Set<SquareBaseWall>(suppressedWalls);

	if (options.includeCornerTrim !== false) {
		addInterlockingCornerBoards(ctx, exteriorGroup, frontExteriorGroup, {
			claddingOffset: cladOffset,
			namePrefix: options.namePrefix,
			// Run the knutbrädor unbroken down to the base trim's bottom
			// drip edge; the base trim butts against them (same depth slab).
			bottomExtensionM:
				options.includeBaseTrim !== false
					? baseTrimH - baseTrimTopAboveWallBase
					: 0,
			suppressedWalls,
		});
	}

	if (options.includeBaseTrim !== false) {
		const cladOuterFaceX = ctx.W / 2 + cladOffset + CLAD_T / 2;
		const cladOuterFaceZ = ctx.L / 2 + cladOffset + CLAD_T / 2;
		const baseTrimD = CLAD_T;
		const baseTrimY = ctx.wallBase - baseTrimH / 2 + baseTrimTopAboveWallBase;
		// Butt into the knutbrädor at each end instead of running through
		// their bottom ends — both live one cladding-thickness proud of the
		// panel, in the same slab.
		const frontBackBaseTrimW = (cladOuterFaceX - CORNER_BOARD_W_M) * 2;
		const sideBaseTrimL = (cladOuterFaceZ - CORNER_BOARD_W_M) * 2;

		// Skip the base trim on any wall with a deck attached — the deck boards
		// run outward through this same slab, so a trim board there would clip.
		if (!suppressed.has("front")) {
			addBox(
				frontBackBaseTrimW,
				baseTrimH,
				baseTrimD,
				matCornerTrimHorizontal,
				0,
				baseTrimY,
				cladOuterFaceZ + baseTrimD / 2,
				frontExteriorGroup,
			).name = `detail/base-trim/${options.namePrefix}-front`;
		}
		if (!suppressed.has("back")) {
			addBox(
				frontBackBaseTrimW,
				baseTrimH,
				baseTrimD,
				matCornerTrimHorizontal,
				0,
				baseTrimY,
				-cladOuterFaceZ - baseTrimD / 2,
				exteriorGroup,
			).name = `detail/base-trim/${options.namePrefix}-back`;
		}
		if (!suppressed.has("left")) {
			addBox(
				baseTrimD,
				baseTrimH,
				sideBaseTrimL,
				matCornerTrimHorizontal,
				-cladOuterFaceX - baseTrimD / 2,
				baseTrimY,
				0,
				exteriorGroup,
			).name = `detail/base-trim/${options.namePrefix}-left`;
		}
		if (!suppressed.has("right")) {
			addBox(
				baseTrimD,
				baseTrimH,
				sideBaseTrimL,
				matCornerTrimHorizontal,
				cladOuterFaceX + baseTrimD / 2,
				baseTrimY,
				0,
				exteriorGroup,
			).name = `detail/base-trim/${options.namePrefix}-right`;
		}
	}

	return { wbThick };
}


/**
 * The outside of an indoor sauna (issue 174).
 *
 * A wall that stands against the room's own wall gets a träfiberskiva on the
 * studs and a 20 mm air gap to that wall (ISOVER, "Bygga bastu"). A wall that
 * stands free in the room is seen from the room, so it gets a panel directly
 * on the studs and foder round its openings. No vindskydd, läkt, corner or
 * base boards: the room is not weather.
 */
export function buildIndoorOuterLayers(
	ctx: SquareBaseBuildContext,
	framingResults: Map<SquareBaseWall, WallFramingResult>,
	exteriorGroup: THREE.Group,
	frontExteriorGroup: THREE.Group,
	options: { namePrefix: string; existingWalls: readonly SquareBaseWall[] },
) {
	const existing = new Set(options.existingWalls);
	const exteriorOffset = CLAD_T / 2;
	for (const wall of ["front", "back", "left", "right"] as const) {
		const result = framingResults.get(wall);
		if (!result) continue;
		const group = wall === "front" ? frontExteriorGroup : exteriorGroup;
		if (existing.has(wall)) {
			renderOuterLayerStrips(
				{
					wall,
					W: ctx.W,
					L: ctx.L,
					wallBase: ctx.wallBase,
					roofGeometry: ctx.roofGeometry,
					offset: INDOOR_FIBREBOARD_T_M / 2,
					material: matWindBarrier,
					group,
					renderGlass: false,
				},
				result.surfaceOpenings,
				ctx.roofGeometry.getMaxWallHeight(wall),
				INDOOR_FIBREBOARD_T_M,
			);
			continue;
		}
		renderExteriorCladdingStrips(
			{
				wall,
				W: ctx.W,
				L: ctx.L,
				wallBase: ctx.wallBase,
				roofGeometry: ctx.roofGeometry,
				offset: exteriorOffset,
				material: matExteriorCladding,
				group,
				cornerExtensionM: wall === "front" || wall === "back" ? CLAD_T : 0,
			},
			result.surfaceOpenings,
			ctx.roofGeometry.getMaxWallHeight(wall),
			CLAD_T,
		);
		for (const opening of mergeOpeningsForTrim(result.surfaceOpenings)) {
			addExteriorOpeningTrimForWall(ctx, group, wall, opening, {
				claddingOffset: exteriorOffset,
				claddingThickness: CLAD_T,
				exteriorTrimDepth: 0.022,
				exteriorTrimWidth: 0.07,
				namePrefix: `${options.namePrefix}-indoor-${wall}-${opening.type}-${opening.id}`,
			});
		}
		for (const opening of result.surfaceOpenings) {
			if (!isGlazedWindow(opening)) continue;
			addWindowFrameForWall(ctx, group, wall, opening, {
				facadeOuterOffsetM: exteriorOffset + CLAD_T / 2,
				namePrefix: `${options.namePrefix}-indoor-${wall}-${opening.id}`,
			});
		}
	}
}
